// Tiny procedural chiptune engine. Schedules square-wave bass notes + a
// pulse-wave arpeggio against a single AudioContext. Per-planet patterns
// give each level its own mood without shipping any audio files.

import { DinoId } from '../data/dinos';

let ctx: AudioContext | null = null;
let muted = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      const Ctor: typeof AudioContext =
        // @ts-expect-error legacy webkit prefix
        window.AudioContext || window.webkitAudioContext;
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
  }
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }
  return ctx;
}

interface Pattern {
  /** 8 sixteenth-note bass notes (Hz; 0 = rest). */
  bass: number[];
  /** 16 sixteenth-note melody notes (Hz; 0 = rest). */
  melody: number[];
  /** Beats per minute — controls how fast the loop ticks. */
  bpm: number;
  /** Optional 16 sixteenth-note harmony notes (Hz; 0 = rest). Layered as a
   *  third voice using a sine pad with sustained envelope — adds chord
   *  warmth so the bass+melody texture isn't naked. Used by Biscuit's
   *  level to bury any residual staccato in a continuous chord wash. */
  harmony?: number[];
  /** Optional melody-note sustain multiplier of one sixteenth (default 0.9
   *  = staccato). Bump >1 for legato. */
  melodyDurMul?: number;
  /** Optional bass-note sustain multiplier of one sixteenth (default 1.8). */
  bassDurMul?: number;
  /** Optional waveform override for the melody synth (default 'triangle'). */
  melodyWave?: OscillatorType;
  /** When true, melody uses a proper ADSR envelope (attack-decay-sustain-
   *  release) instead of the default attack-then-immediate-decay. The
   *  default envelope hits each note like a struck bell — fine for
   *  arpeggios but at slow tempos it reads as morse code (BIP... BIP...
   *  BIP...). ADSR holds at the sustain level so each note is a
   *  continuous tone, which is what Biscuit's level needs. */
  melodySustained?: boolean;
}

// Notes (Hz). C4 = 261.6, etc.
const N = {
  c2: 65.4, e2: 82.4, g2: 98.0, a2: 110.0, b2: 123.5,
  c3: 130.8, d3: 146.8, e3: 164.8, f3: 174.6, g3: 196.0, a3: 220.0,
  c4: 261.6, d4: 293.7, e4: 329.6, f4: 349.2, g4: 392.0,
  a4: 440.0, b4: 493.9, c5: 523.3, d5: 587.3, e5: 659.3,
};

/** Music keys — per-planet plus a 'menu' theme used on the title intro
 *  and the planet-select screen. */
export type MusicKey = DinoId | 'menu';

// Per-planet patterns. All in 4/4, 16 sixteenths per measure.
//   Glacier  — D minor, slow + mystical
//   Inferno  — A minor pentatonic, driving
//   Dunes    — G mixolydian, marching/epic (kept LOW + spacious; previous
//              version sat on d5/e5 with rapid 16ths and was ear-piercing)
//   Canopy   — C major, bouncy/bright
//   Menu     — C major, upbeat anthem for the intro + planet select
const PATTERNS: Record<MusicKey, Pattern> = {
  doux: {
    bpm: 100,
    bass: [N.d3, 0, N.a2, 0, N.f3, 0, N.a2, 0],
    melody: [
      N.d4, 0, N.f4, 0, N.a4, 0, N.f4, 0,
      N.d4, 0, N.e4, 0, N.f4, 0, N.a4, 0,
    ],
  },
  mort: {
    bpm: 132,
    bass: [N.a2, N.a2, N.e3, 0, N.a2, N.a2, N.g2, 0],
    melody: [
      N.a4, N.c5, N.e5, 0, N.a4, N.c5, N.d5, 0,
      N.a4, N.c5, N.e5, 0, N.g4, N.a4, 0, 0,
    ],
  },
  tard: {
    // Three-voice arrangement to bury the morse-code-feeling staccato
    // melody under a wash of bass + sustained chord pad:
    //   1. Walking bass (square wave, eighth notes) — gives it groove
    //   2. Sustained sine-pad harmony chord tones — held throughout, ADSR
    //      so each chord change crossfades into the next
    //   3. Light melodic sawtooth on top — sparse, with melodyDurMul
    //      keeping notes connected
    // Bpm down at 80 for a slow lope.
    bpm: 80,
    melodySustained: true,
    melodyDurMul: 2.4,
    melodyWave: 'sawtooth',
    bass: [N.g2, N.g2, N.d3, N.d3, N.c3, N.c3, N.d3, N.g2],
    // Sparse melody — three notes per phrase, held — gives shape without
    // hammering on every 16th.
    melody: [
      N.g4, 0, 0, 0,   N.b4, 0, 0, 0,
      N.c5, 0, 0, 0,   N.b4, 0, 0, 0,
    ],
    // Sustained chord pad — Gm7-ish chord tones across the measure, held
    // through their full duration so the texture is always full.
    harmony: [
      N.d4, 0, 0, 0,   N.f4, 0, 0, 0,
      N.e4, 0, 0, 0,   N.d4, 0, 0, 0,
    ],
  },
  vita: {
    bpm: 120,
    bass: [N.c3, 0, N.g2, 0, N.a2, 0, N.f3, 0],
    melody: [
      N.c5, N.e5, N.g4, N.e5, N.c5, N.g4, N.a4, N.c5,
      N.f4, N.a4, N.c5, N.a4, N.g4, N.e4, N.c4, 0,
    ],
  },
  menu: {
    // Upbeat C-major arcade anthem. I-V-vi-IV progression, ascending
    // melody with a hooky resolution. Used on Title + CharacterSelect.
    bpm: 138,
    bass: [N.c3, 0, N.g2, 0, N.a2, 0, N.f3, 0],
    melody: [
      N.c4, N.e4, N.g4, N.c5, N.b4, N.g4, N.e4, N.g4,
      N.a4, N.c5, N.e5, N.c5, N.f4, N.a4, N.c5, N.a4,
    ],
  },
};

/**
 * Singleton chiptune player. Uses a look-ahead scheduling loop:
 *   - setInterval ticks every 25ms
 *   - schedules notes 100ms ahead so the oscillator timing stays tight
 */
class MusicPlayer {
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextBeatTime = 0;
  private currentSixteenth = 0;
  private pattern: Pattern | null = null;
  private masterGain: GainNode | null = null;
  /** Currently-playing key. Lets us skip the stop+restart cycle when a
   *  scene calls start() with the same key already in flight (e.g. the
   *  menu music persisting across Title → CharacterSelect → Leaderboard). */
  private currentKey: MusicKey | null = null;

  start(key: MusicKey) {
    if (muted) return;
    // Already playing this exact track — leave it alone (avoids the
    // tiny gap when transitioning between scenes that share the menu loop).
    if (this.currentKey === key && this.timer) return;
    const c = getCtx();
    if (!c) return;
    this.stop(); // clean state if we're switching tracks
    this.pattern = PATTERNS[key];
    this.currentKey = key;
    this.nextBeatTime = c.currentTime + 0.05;
    this.currentSixteenth = 0;
    this.masterGain = c.createGain();
    this.masterGain.gain.value = 0.18;
    this.masterGain.connect(c.destination);
    this.timer = setInterval(() => this.scheduler(), 25);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.masterGain) {
      // Fade out so we don't click.
      const c = getCtx();
      if (c) {
        const g = this.masterGain;
        g.gain.setValueAtTime(g.gain.value, c.currentTime);
        g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.15);
        setTimeout(() => g.disconnect(), 200);
      }
      this.masterGain = null;
    }
    this.pattern = null;
    this.currentKey = null;
  }

  setMuted(m: boolean) {
    muted = m;
    if (m) this.stop();
  }

  isPlaying() {
    return !!this.timer;
  }

  private scheduler() {
    const c = getCtx();
    if (!c || !this.pattern || !this.masterGain) return;
    const sixteenthSec = 60 / this.pattern.bpm / 4;
    while (this.nextBeatTime < c.currentTime + 0.1) {
      this.scheduleSixteenth(this.currentSixteenth, this.nextBeatTime, sixteenthSec);
      this.currentSixteenth = (this.currentSixteenth + 1) % 16;
      this.nextBeatTime += sixteenthSec;
    }
  }

  private scheduleSixteenth(idx: number, time: number, dur: number) {
    const c = getCtx();
    const p = this.pattern;
    const out = this.masterGain;
    if (!c || !p || !out) return;
    const bassMul = p.bassDurMul ?? 1.8;
    const melMul = p.melodyDurMul ?? 0.9;
    const melWave: OscillatorType = p.melodyWave ?? 'triangle';
    const sustained = p.melodySustained ?? false;
    // Bass plays on the eighth-note grid (0, 2, 4, ...).
    if (idx % 2 === 0) {
      const bassFreq = p.bass[idx / 2];
      if (bassFreq) this.scheduleNote(c, out, bassFreq, time, dur * bassMul, 'square', 0.16, false);
    }
    // Melody on every sixteenth.
    const melFreq = p.melody[idx];
    if (melFreq) this.scheduleNote(c, out, melFreq, time, dur * melMul, melWave, 0.10, sustained);
    // Harmony (third voice) — sustained sine pad. Uses generous duration
    // (~5 sixteenths) so chord tones overlap and crossfade between changes,
    // creating a continuous wash underneath.
    if (p.harmony) {
      const harFreq = p.harmony[idx];
      if (harFreq) this.scheduleNote(c, out, harFreq, time, dur * 5, 'sine', 0.07, true);
    }
  }

  /**
   * Schedule a single note. Two envelope shapes:
   *   - DEFAULT (sustained=false): attack → exponential decay. Plucked /
   *     bell-like. Good for arpeggios + fast lines.
   *   - SUSTAINED (sustained=true): proper ADSR. Attacks to peak, decays
   *     to ~70% sustain level, holds through `duration - release`, then
   *     ramps to silence. Sounds like a held tone instead of a dot.
   */
  private scheduleNote(
    c: AudioContext,
    out: AudioNode,
    freq: number,
    time: number,
    duration: number,
    type: OscillatorType,
    peak: number,
    sustained: boolean,
  ) {
    const osc = c.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);
    const env = c.createGain();
    env.gain.setValueAtTime(0, time);
    if (sustained) {
      const attack = 0.025;
      const decay = 0.06;
      const sustainLvl = peak * 0.7;
      const release = Math.min(0.15, duration * 0.25);
      const sustainEnd = Math.max(time + attack + decay + 0.01, time + duration - release);
      env.gain.linearRampToValueAtTime(peak, time + attack);
      env.gain.linearRampToValueAtTime(sustainLvl, time + attack + decay);
      env.gain.setValueAtTime(sustainLvl, sustainEnd);
      env.gain.linearRampToValueAtTime(0.0001, sustainEnd + release);
      osc.connect(env).connect(out);
      osc.start(time);
      osc.stop(sustainEnd + release + 0.02);
    } else {
      env.gain.linearRampToValueAtTime(peak, time + 0.01);
      env.gain.exponentialRampToValueAtTime(0.0001, time + duration);
      osc.connect(env).connect(out);
      osc.start(time);
      osc.stop(time + duration + 0.02);
    }
  }
}

export const Music = new MusicPlayer();
